import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  computed,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';

import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import {
  StatusFilterComponent,
  ActionButton,
} from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  FileUploadComponent,
  type FileUploadData,
} from '@shared/components/file-upload/file-upload.component';

import { PermissionService } from '@app/core/services/permission.service';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  formatDate,
  formatDateTime,
} from '@app/shared/utils/date-formatter.util';
import type { LocalTime } from '@app/api/promotion-service/model/localTime';

import { OfferService } from '../../services/offer.service';
import type { FileDTO } from '@app/api/promotion-service/model/fileDTO';
import type { OfferImportResult } from '@app/api/promotion-service/model/offerImportResult';
import { OfferImportService } from '@app/api/promotion-service/api/offerImport.service';
import {
  OfferListItem,
  OfferSearchParams,
  OfferUiStatus,
} from '../../models/offer.interface';

@Component({
  selector: 'app-offer-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    DialogModule,
    CardModule,
    FileUploadComponent,
    SearchFormComponent,
    AdvancedDataTableComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  templateUrl: './offer-list.component.html',
  styleUrl: './offer-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class OfferListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly offerService = inject(OfferService);
  private readonly offerImportService = inject(OfferImportService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);

  private readonly destroy$ = new Subject<void>();

  offers = signal<OfferListItem[]>([]);
  loading = signal<boolean>(false);
  totalItems = signal<number>(0);

  searchParams = signal<OfferSearchParams>({
    page: 0,
    size: 20,
  });

  showStatusChangeDialog = false;
  offerToChangeStatus: OfferListItem | null = null;

  readonly DEFAULT_PAGE_SIZE = 20;

  // Template download dialog state
  showTemplateDialog = signal(false);
  templateLoading = signal(false);
  templates = signal<FileDTO[]>([]);
  selectedTemplateFile = signal<FileDTO | null>(null);
  private readonly cachedTemplateBlob = signal<Blob | null>(null);

  // Create (import offers) dialog state
  showCreateDialog = signal(false);
  createLoading = signal(false);
  createUpload = signal<FileUploadData | null>(null);

  // Enable/Disable confirmation buttons
  statusChangeButtons: ConfirmationButton[] = [
    {
      label: 'common.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'common.actions.confirm',
      action: 'confirm',
      severity: 'info',
    },
  ];

  // Permission flags
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('marketing.promotion.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('marketing.promotion.edit');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('marketing.promotion.approve');
  });

  /** Same permission as creating offers (marketing). */
  canDownloadTemplate = computed(() => this.canCreate());

  primaryButton = computed<ActionButton>(() => ({
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
    outlined: false,
  }));

  searchFields = computed<FormFieldConfig[]>(() => [
    {
      key: 'code',
      label: 'offer.list.fields.code',
      type: 'text',
      placeholder: 'offer.list.placeholders.code',
    },
    {
      key: 'displayName',
      label: 'offer.list.fields.name',
      type: 'text',
      placeholder: 'offer.list.placeholders.name',
    },
    {
      key: 'status',
      label: 'offer.list.fields.status',
      type: 'select',
      placeholder: 'offer.list.placeholders.status',
      options: [
        { label: 'offer.status.active', value: 'active' },
        { label: 'offer.status.inactive', value: 'inactive' },
        { label: 'offer.status.draft', value: 'draft' },
      ],
    },
    {
      key: 'kind',
      label: 'offer.list.fields.kind',
      type: 'select',
      placeholder: 'offer.list.placeholders.kind',
      options: this.getKindOptions(),
    },
  ]);

  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  @ViewChild('importFileUpload') importFileUpload?: FileUploadComponent;

  ngOnInit(): void {
    this.loadOffers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadOffers(): void {
    this.loading.set(true);

    this.offerService
      .getOffers(this.searchParams())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ items, totalCount }) => {
          this.offers.set(items);
          this.totalItems.set(totalCount);
          this.loading.set(false);
        },
        error: (error: HttpErrorResponse) => {
          this.loading.set(false);

          // Không show lỗi nếu coi như không có lỗi "business":
          // - status = 0 (mạng tạm), hoặc
          // - status 2xx/3xx, hoặc
          // - status < 400 nói chung.
          if (!error.status || error.status < 400) {
            return;
          }

          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'offer.messages.error.load_failed',
            ),
          });
        },
      });
  }

  onSearch(event: any): void {
    const params: OfferSearchParams = {
      ...this.searchParams(),
      code: event.code,
      displayName: event.displayName,
      status: event.status,
      kind: event.kind,
      page: 0,
    };
    this.searchParams.set(params);
    this.loadOffers();
  }

  onReset(): void {
    const params: OfferSearchParams = {
      page: 0,
      size: this.searchParams().size ?? this.DEFAULT_PAGE_SIZE,
      sortBy: this.searchParams().sortBy,
      sortOrder: this.searchParams().sortOrder,
    };
    this.searchParams.set(params);
    this.loadOffers();
  }

  onDownloadTemplate(): void {
    if (!this.canDownloadTemplate()) {
      return;
    }

    this.templateLoading.set(true);
    this.offerImportService
      .downloadTemplate('response', false, { httpHeaderAccept: '*/*' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const disposition = res.headers.get('content-disposition') ?? '';
          const fileName =
            this.getFileNameFromDisposition(disposition) ||
            'offer-template.xlsx';
          this.cachedTemplateBlob.set(res.body as Blob);
          this.templates.set([{ name: fileName }]);
          this.selectedTemplateFile.set(null);
          this.templateLoading.set(false);
          this.showTemplateDialog.set(true);
        },
        error: () => {
          this.templateLoading.set(false);
          this.templates.set([]);
          this.cachedTemplateBlob.set(null);
          this.showTemplateDialog.set(true);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant('offer.template.load_error'),
          });
        },
      });
  }

  onCloseTemplateDialog(): void {
    this.showTemplateDialog.set(false);
    this.selectedTemplateFile.set(null);
    this.cachedTemplateBlob.set(null);
  }

  selectTemplateFile(file: FileDTO): void {
    this.selectedTemplateFile.set(file);
  }

  downloadTemplateFile(file: FileDTO): void {
    if (!file) {
      return;
    }
    const cachedBlob = this.cachedTemplateBlob();
    if (cachedBlob) {
      this.triggerTemplateDownload(
        cachedBlob,
        file.name || 'offer-template.xlsx',
      );
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.messages.success'),
        detail: this.translateService.instant(
          'offer.template.download_success',
        ),
      });
      this.showTemplateDialog.set(false);
      this.selectedTemplateFile.set(null);
      this.cachedTemplateBlob.set(null);
      return;
    }

    this.templateLoading.set(true);
    this.offerImportService
      .downloadTemplate('response', false, { httpHeaderAccept: '*/*' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const blob = res.body as Blob;
          const disposition = res.headers.get('content-disposition') ?? '';
          const fileName = this.getFileNameFromDisposition(disposition);
          this.triggerTemplateDownload(
            blob,
            fileName || file.name || 'offer-template.xlsx',
          );

          this.templateLoading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'offer.template.download_success',
            ),
          });
          this.showTemplateDialog.set(false);
          this.selectedTemplateFile.set(null);
        },
        error: () => {
          this.templateLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'offer.template.download_error',
            ),
          });
        },
      });
  }

  private triggerTemplateDownload(blob: Blob, fileName: string): void {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  private getFileNameFromDisposition(disposition: string): string {
    if (!disposition) return '';
    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].trim().replaceAll(/["']/g, ''));
    }
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    return match?.[1]?.trim() ?? '';
  }

  downloadSelectedTemplateFile(): void {
    const file = this.selectedTemplateFile();
    if (!file) return;
    this.downloadTemplateFile(file);
  }

  onPageChange(event: any): void {
    const params: OfferSearchParams = {
      ...this.searchParams(),
      page: event.page,
      size: event.rows,
    };
    this.searchParams.set(params);
    this.loadOffers();
  }

  onSort(event: any): void {
    const params: OfferSearchParams = {
      ...this.searchParams(),
      sortBy: event.field,
      sortOrder: event.order === 1 ? 'asc' : 'desc',
      page: 0,
    };
    this.searchParams.set(params);
    this.loadOffers();
  }

  onAddOffer(): void {
    if (!this.canCreate()) return;
    this.router.navigate(['/marketing/promotion/create']);
  }

  onImportOffers(): void {
    if (!this.canCreate()) return;
    this.resetImportDialogState();
    this.showCreateDialog.set(true);
  }

  onCloseCreateDialog(): void {
    this.resetImportDialogState();
    this.showCreateDialog.set(false);
    this.createLoading.set(false);
  }

  canSubmitImport(): boolean {
    return !!this.createUpload()?.file;
  }

  private resetImportDialogState(): void {
    this.importFileUpload?.reset();
    this.createUpload.set(null);
  }

  onCreateFileChanged(file: FileUploadData | null): void {
    this.createUpload.set(file);
  }

  private getValidatedImportFile(): File | null {
    const upload = this.createUpload();
    const file = upload?.file;
    if (!file) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('offer.validation.file_required'),
      });
      return null;
    }

    const fileName = file.name ?? '';
    const ext = fileName.includes('.')
      ? fileName.split('.').pop()!.toLowerCase()
      : '';
    const allowed = ext === 'xlsx' || ext === 'xls';
    if (!allowed) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'offer.validation.invalid_file_type_detail',
        ),
      });
      return null;
    }

    return file;
  }

  onConfirmCreateFromFile(): void {
    if (!this.canCreate()) return;

    const file = this.getValidatedImportFile();
    if (!file) return;

    this.createLoading.set(true);
    this.offerImportService
      .importExcel(file, false, 'body', false, {
        httpHeaderAccept: '*/*',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (_res: OfferImportResult) => {
          this.createLoading.set(false);
          this.resetImportDialogState();
          this.showCreateDialog.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'offer.messages.import_success',
            ),
            detail: this.translateService.instant('common.messages.success'),
          });
          this.loadOffers();
        },
        error: () => {
          this.createLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'offer.messages.import_error',
            ),
            detail: this.translateService.instant(
              'offer.messages.upload_error',
            ),
          });
        },
      });
  }

  onEdit(offer: OfferListItem): void {
    if (!this.canUpdate()) return;
    this.router.navigate(['/marketing/promotion/edit', offer.id]);
  }

  onToggleStatus(offer: OfferListItem): void {
    if (!this.canApprove()) return;
    this.offerToChangeStatus = offer;
    this.showStatusChangeDialog = true;
  }

  getStatusChangeMessage(): string {
    if (!this.offerToChangeStatus) return '';
    const next =
      this.offerToChangeStatus.status === 'active' ? 'inactive' : 'active';
    const key =
      next === 'active'
        ? 'promotion_management.messages.confirm_activate'
        : 'promotion_management.messages.confirm_deactivate';
    return this.translateService.instant(key, {
      name: this.offerToChangeStatus.code,
    });
  }

  onStatusChangeConfirmation(action: string): void {
    if (action !== 'confirm' || !this.offerToChangeStatus) {
      this.showStatusChangeDialog = false;
      this.offerToChangeStatus = null;
      return;
    }

    const current = this.offerToChangeStatus.status;
    const next: OfferUiStatus = current === 'active' ? 'inactive' : 'active';

    this.offerService
      .toggleOfferStatus(this.offerToChangeStatus.id, next)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showStatusChangeDialog = false;
          this.offerToChangeStatus = null;
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'promotion_management.messages.status_update_success',
            ),
          });
          this.loadOffers();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'promotion_management.messages.status_update_error',
            ),
          });
        },
      });
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  defaultColumns = computed<TableColumn[]>(() => [
    {
      label: 'offer.list.fields.code',
      prop: 'code',
      width: 140,
      align: 'left',
      headerAlign: 'center',
      sortable: false,
    },
    {
      label: 'offer.list.fields.display_name',
      prop: 'displayName',
      width: 220,
      align: 'left',
      headerAlign: 'center',
      sortable: false,
    },
    {
      label: 'offer.list.fields.kind',
      prop: 'kind',
      type: 'custom',
      template: 'kindTemplate',
      width: 260,
      align: 'left',
      headerAlign: 'center',
      sortable: false,
    },
    {
      label: 'offer.list.fields.validity',
      prop: 'validity',
      type: 'custom',
      template: 'validityTemplate',
      width: 300,
      align: 'left',
      headerAlign: 'center',
      sortable: false,
    },
    {
      label: 'offer.list.fields.updated_at',
      prop: 'updatedAt',
      type: 'custom',
      template: 'datetimeTemplate',
      width: 170,
      align: 'left',
      headerAlign: 'center',
      sortable: false,
    },
    {
      label: 'offer.list.fields.status',
      prop: 'status',
      type: 'status',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
    },
    {
      label: 'offer.list.fields.actions',
      prop: 'actions',
      type: 'actions',
      width: 200,
      align: 'center',
      headerAlign: 'center',
    },
  ]);

  flexibleColumns = computed<TableColumn[]>(() => []);

  getStatusLabel(status: OfferUiStatus): string {
    if (status === 'active') return 'offer.status.active';
    if (status === 'inactive') return 'offer.status.inactive';
    return 'offer.status.draft';
  }

  getStatusSeverity(status: OfferUiStatus): any {
    if (status === 'active') return 'success';
    if (status === 'inactive') return 'secondary';
    return 'info';
  }

  getStatusStyle(status: OfferUiStatus): any {
    if (status === 'active') {
      return {
        'background-color': '#ecfdf5',
        color: '#047857',
        border: 'none',
      };
    }
    if (status === 'inactive') {
      return {
        'background-color': '#f9fafb',
        color: '#6b7280',
        border: 'none',
      };
    }
    return { 'background-color': '#eff6ff', color: '#2563eb', border: 'none' };
  }

  getKindDisplay(kind?: string): string {
    if (!kind) return '-';
    const key = this.KIND_LABELS[kind] ?? kind;
    const translated = this.translateService.instant(key);
    return translated === key ? kind : translated;
  }

  getKindSeverity(kind?: string): any {
    if (!kind) return 'secondary';
    return 'info';
  }

  getValidityDisplay(row: OfferListItem): string {
    const start = row.startDate ? formatDate(row.startDate) : '';
    const end = row.endDate ? formatDate(row.endDate) : '';
    if (!start && !end) {
      return '-';
    }
    const range =
      start && end ? `${start} – ${end}` : start || end;
    const daily = this.formatDailyTimeRange(row.startTime, row.endTime);
    return daily ? `${range}\n${daily}` : range;
  }

  private formatDailyTimeRange(
    start?: LocalTime,
    end?: LocalTime,
  ): string {
    const s = this.formatLocalTime(start);
    const e = this.formatLocalTime(end);
    if (!s && !e) {
      return '';
    }
    if (s && e) {
      return `${s} – ${e}`;
    }
    return s || e;
  }

  private formatLocalTime(time?: LocalTime | string): string {
    const parts = this.parseLocalTimeParts(time);
    if (!parts) {
      return '';
    }
    const hh = String(parts.hour).padStart(2, '0');
    const mm = String(parts.minute).padStart(2, '0');
    const ss = String(parts.second).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }

  private parseLocalTimeParts(
    time?: LocalTime | string,
  ): { hour: number; minute: number; second: number } | null {
    if (!time) {
      return null;
    }
    if (typeof time === 'string') {
      const m = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/.exec(time.trim());
      if (!m) {
        return null;
      }
      return {
        hour: Number(m[1]),
        minute: Number(m[2]),
        second: Number(m[3] ?? '0'),
      };
    }
    return {
      hour: time.hour ?? 0,
      minute: time.minute ?? 0,
      second: time.second ?? 0,
    };
  }

  // Supported kinds only (matches OFFER_TYPE_OPTIONS). Unknown kinds on legacy
  // rows fall back to the raw value via getKindDisplay.
  private readonly KIND_LABELS: Record<string, string> = {
    PERIODIC_DISCOUNT: 'offer.type.periodic_discount',
    MULTIBUY: 'offer.type.multibuy',
    TOTAL_DISCOUNT: 'offer.type.total_discount',
    LINE_DISCOUNT: 'offer.type.line_discount',
    TENDER: 'offer.type.tender',
    MIX_MATCH: 'offer.type.mix_match',
    PROMOTIONAL_PRICE: 'offer.type.promotional_price',
  };

  private getKindOptions(): { label: string; value: any }[] {
    return Object.entries(this.KIND_LABELS).map(([value, label]) => ({
      label,
      value,
    }));
  }

  formatDateTime = formatDateTime;
}
